import { ErrorHandler, LOCALE_ID, inject, provideAppInitializer } from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { MAT_CARD_CONFIG, MatCardConfig } from "@angular/material/card";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { AppConfig } from "app/common/app-config";
import { InfoClient } from "app/dal/generated/clients";
import { UploadProgressInterceptor } from "app/storage/storage.service";
import { ApiConfiguration } from "app/dal/generated/api-configuration";
import { combineLatest, switchMap, tap } from "rxjs";
import { provideAgGrid } from "./app/common-ux/data-grid/ag-grid-setup";
import { appRoutes } from "./app/app.routes";
import { AppComponent } from "./app/app.component";
import { AppService } from "./app/app.service";
import { AutoDateConversionInterceptor } from "./app/auth/auto-date-conversion.interceptor";
import { AuthenticationService } from "./app/auth/authentication.service";
import { BearerTokenInterceptor } from "./app/auth/bearer-token.interceptor";
import { SessionTimeoutInterceptor } from "./app/auth/session-timeout-interceptor";
import { provideExistingUserObserver } from "./app/auth/user-observer";
import { provideExistingDialogObserver } from "./app/common-ux/dialog/dialog-observer";
import { HttpErrorInterceptor } from "./app/http-error.interceptor";
import { SentryLogger } from "./app/sentry-logger";
import { BreezeService } from "./app/dal/breeze.service";
import { TenantAuthorisationService } from "./app/tenant/module/tenant-authorisation.service";
import { TenantIdInterceptor } from "./app/tenant/module/tenant-id.interceptor";
import { TenantService } from "./app/tenant/module/tenant.service";

bootstrapApplication(AppComponent, {
    providers: [
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        provideAnimations(),
        provideRouter(appRoutes),
        TenantService,
        TenantAuthorisationService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: LOCALE_ID,
            useValue: "en-AU",
        },
        {
            provide: MAT_CARD_CONFIG,
            useValue: {
                appearance: "outlined",
            } as MatCardConfig,
        },
        provideAppInitializer(() => {
            const appConfig = inject(AppConfig);
            const apiConfiguration = inject(ApiConfiguration);
            const authenticationService = inject(AuthenticationService);
            const infoClient = inject(InfoClient);
            return appConfig.config$.pipe(
                tap((config) => {
                    apiConfiguration.rootUrl = config.serverEndpoint;
                    // Other initialisers can start before the root URL is set
                    void authenticationService.checkForCurrentSession();
                    // Prime the server for requests (if we've scaled to zero)
                    infoClient.getInfo().subscribe();
                }),
            );
        }),
        provideAgGrid(),
        {
            provide: HTTP_INTERCEPTORS,
            multi: true,
            useClass: HttpErrorInterceptor,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BearerTokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionTimeoutInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AutoDateConversionInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UploadProgressInterceptor,
            multi: true,
        },
        // We only want this to apply within the tenant area, but HTTP_INTERCEPTORS cannot be
        // scoped to a lazy-loaded route's injector. So it's registered here at the app root
        // (most of the time it will be needed pretty quickly anyway), keeping the tenant shell
        // itself lazy loaded. See https://github.com/angular/angular/issues/36974
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TenantIdInterceptor,
            multi: true,
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideExistingUserObserver(SentryLogger),
        provideExistingDialogObserver(SentryLogger),
        provideHttpClient(withInterceptorsFromDi()),
        provideAppInitializer(() => {
            const tenantService = inject(TenantService);
            const breezeService = inject(BreezeService);

            // Clear the breeze cache on a tenant switch
            tenantService.currentTenant$
                .pipe(switchMap(() => breezeService.initialisedEntityManager$))
                .subscribe((em) => {
                    em.clear();
                });
        }),
        provideAppInitializer(() => {
            const tenantService = inject(TenantService);
            const appService = inject(AppService);
            combineLatest([
                tenantService.currentTenant$,
                appService.pageTitle$,
            ]).subscribe(([tenant, pageTitle]) => {
                let title = pageTitle ?? "";
                if (title) {
                    title += " | ";
                }
                title += tenant ? tenant.name : "Orchestrate";
                document.title = title;
            });
        }),
    ],
}).catch((err: unknown) => console.log(err));
